import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload, X, Sparkles, Award, Shield, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProject, useCreateProject, useUpdateProject } from "@/hooks/api/use-projects";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import type { ProjectImage } from "@/api/types/project.types";
import { ProtectedImage } from "@/hooks/use-protected-image";

const AddEditProject = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  
  const isEditing = !!id;
  const projectId = id ? parseInt(id, 10) : 0;
  
  const { data: projectData, isLoading: isLoadingProject } = useProject(projectId);
  
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  
  const project = projectData?.data;
  
  const [formData, setFormData] = useState({
    title_ar: "",
    title_en: "",
    description_ar: "",
    description_en: "",
    location_ar: "",
    location_en: "",
    completed_year: "",
    completed_month: "",
    completed_day: "",
  });

  const [images, setImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<ProjectImage[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load project data when editing
  useEffect(() => {
    if (isEditing && project) {
      const title = typeof project.title === 'string' 
        ? { ar: project.title, en: project.title }
        : { ar: project.title.ar || '', en: project.title.en || '' };
      const description = project.description 
        ? (typeof project.description === 'string'
          ? { ar: project.description, en: project.description }
          : { ar: project.description.ar || '', en: project.description.en || '' })
        : { ar: '', en: '' };
      const location = project.location
        ? (typeof project.location === 'string'
          ? { ar: project.location, en: project.location }
          : { ar: project.location.ar || '', en: project.location.en || '' })
        : { ar: '', en: '' };
      
      const completedDate = project.completed_date ? new Date(project.completed_date) : null;
      setFormData({
        title_ar: title.ar,
        title_en: title.en,
        description_ar: description.ar,
        description_en: description.en,
        location_ar: location.ar,
        location_en: location.en,
        completed_year: completedDate ? completedDate.getFullYear().toString() : "",
        completed_month: completedDate ? (completedDate.getMonth() + 1).toString().padStart(2, '0') : "",
        completed_day: completedDate ? completedDate.getDate().toString().padStart(2, '0') : "",
      });
      
      // Load existing images
      if (project.images) {
        setExistingImages(project.images);
      }
    }
  }, [isEditing, project]);

  // Image handling functions
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== fileArray.length) {
      toast({
        title: t('common.error') || 'خطأ',
        description: 'يُرجى اختيار ملفات صور فقط',
        variant: 'destructive',
      });
      return;
    }
    
    setImages(prev => [...prev, ...imageFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    handleFileSelect(e.dataTransfer.files);
  };

  const removeNewImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (imageId: number) => {
    setExistingImages(prev => prev.filter(img => img.id !== imageId));
    setImagesToDelete(prev => [...prev, imageId]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title_ar.trim() || !formData.title_en.trim()) {
      toast({
        title: t('common.error') || 'حدث خطأ',
        description: t('portfolio.projectName') || 'يرجى إدخال اسم المشروع',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.description_ar.trim() || !formData.description_en.trim()) {
      toast({
        title: t('common.error') || 'حدث خطأ',
        description: t('portfolio.description') || 'يرجى إدخال وصف المشروع',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.location_ar.trim() || !formData.location_en.trim()) {
      toast({
        title: t('common.error') || 'حدث خطأ',
        description: t('portfolio.locationAr') || 'يرجى إدخال الموقع',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.completed_year || !formData.completed_month || !formData.completed_day) {
      toast({
        title: t('common.error') || 'حدث خطأ',
        description: t('portfolio.completedDate') || 'يرجى اختيار تاريخ الإنجاز',
        variant: 'destructive',
      });
      return;
    }

    const completedDate = `${formData.completed_year}-${formData.completed_month}-${formData.completed_day}`;
    const projectData = {
      title: {
        ar: formData.title_ar.trim(),
        en: formData.title_en.trim(),
      },
      description: {
        ar: formData.description_ar.trim(),
        en: formData.description_en.trim(),
      },
      location: {
        ar: formData.location_ar.trim(),
        en: formData.location_en.trim(),
      },
      completed_date: completedDate,
    };

    // Prepare FormData for file uploads
    const formDataToSend = new FormData();
    formDataToSend.append('title[ar]', projectData.title.ar);
    formDataToSend.append('title[en]', projectData.title.en);
    formDataToSend.append('description[ar]', projectData.description.ar);
    formDataToSend.append('description[en]', projectData.description.en);
    formDataToSend.append('location[ar]', projectData.location.ar);
    formDataToSend.append('location[en]', projectData.location.en);
    formDataToSend.append('completed_date', projectData.completed_date);

    // Add new images
    images.forEach((image, index) => {
      formDataToSend.append(`images[${index}]`, image);
    });

    // Add images to delete (for updates)
    if (isEditing && imagesToDelete.length > 0) {
      imagesToDelete.forEach((imageId, index) => {
        formDataToSend.append(`delete_images[${index}]`, imageId.toString());
      });
    }

    if (isEditing) {
      console.log('Updating project with FormData:', {
        projectId,
        formDataToSend,
        imagesCount: images.length,
        imagesToDeleteCount: imagesToDelete.length
      });

      updateProject.mutate(
        { id: projectId, data: formDataToSend },
        {
          onSuccess: (response) => {
            console.log('Update success:', response);
            toast({
              title: t('common.success') || 'نجاح',
              description: t('portfolio.projectUpdated') || 'تم تحديث المشروع بنجاح',
            });
            navigate('/dashboard/portfolio');
          },
          onError: (error: any) => {
            console.error('Update error:', error);
            console.error('Error response:', error.response);
            toast({
              title: t('common.error') || 'حدث خطأ',
              description: error.response?.data?.message || t('portfolio.updateError') || 'فشل تحديث المشروع',
              variant: 'destructive',
            });
          }
        }
      );
    } else {
      createProject.mutate(formDataToSend, {
        onSuccess: () => {
          toast({
            title: t('common.success') || 'نجاح',
            description: t('portfolio.projectAdded') || 'تم إضافة المشروع بنجاح',
          });
          navigate('/dashboard/portfolio');
        },
        onError: (error: any) => {
          toast({
            title: t('common.error') || 'حدث خطأ',
            description: error.response?.data?.message || t('portfolio.addError') || 'فشل إضافة المشروع',
            variant: 'destructive',
          });
        }
      });
    }
  };

  if (isEditing && isLoadingProject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-8">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/portfolio')} className="bg-white border-slate-200 hover:bg-slate-50">
                <ArrowLeft size={20} />
              </Button>
              <Skeleton className="h-8 w-48" />
            </div>
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10">
        {/* Enhanced Header */}
        <div className="bg-white/50 backdrop-blur-sm border-b border-slate-200">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/dashboard/portfolio')} 
                className="bg-white border-slate-200 hover:bg-slate-50"
              >
                <ArrowLeft size={20} />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Award size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">
                    {isEditing 
                      ? (t('portfolio.editProjectTitle') || 'تعديل مشروع')
                      : (t('portfolio.addNewTitle') || 'إضافة مشروع جديد')
                    }
                  </h1>
                  <p className="text-slate-600 mt-1">
                    {isEditing 
                      ? (t('portfolio.editProjectDesc') || 'تعديل تفاصيل المشروع')
                      : (t('portfolio.addProjectDesc') || 'إضافة مشروع جديد إلى معرض أعمالك')
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Form */}
        <div className="container mx-auto px-4 py-8">
          <Card className="bg-white border-slate-200 shadow-xl overflow-hidden rounded-2xl">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Sparkles size={20} className="text-primary" />
                </div>
                <CardTitle className="text-xl text-slate-900">
                  {isEditing 
                    ? (t('portfolio.projectDetails') || 'تفاصيل المشروع')
                    : (t('portfolio.newProjectDetails') || 'تفاصيل المشروع الجديد')
                  }
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-8">
            {/* Title Fields */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Award size={16} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t('portfolio.projectInfo') || 'معلومات المشروع'}
                    </h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="text-red-500">*</span>
                        {t('portfolio.projectNameAr') || 'اسم المشروع (عربي)'}
                      </label>
                      <Input
                        value={formData.title_ar}
                        onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                        required
                        disabled={createProject.isPending || updateProject.isPending}
                        placeholder={t('portfolio.projectNameAr') || 'اسم المشروع (عربي)'}
                        className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="text-red-500">*</span>
                        {t('portfolio.projectNameEn') || 'Project Name (English)'}
                      </label>
                      <Input
                        value={formData.title_en}
                        onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                        required
                        disabled={createProject.isPending || updateProject.isPending}
                        placeholder={t('portfolio.projectNameEn') || 'Project Name (English)'}
                        className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Description Fields */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Sparkles size={16} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t('portfolio.description') || 'وصف المشروع'}
                    </h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="text-red-500">*</span>
                        {t('portfolio.descriptionAr') || 'وصف المشروع (عربي)'}
                      </label>
                      <Textarea
                        value={formData.description_ar}
                        onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                        rows={4}
                        required
                        disabled={createProject.isPending || updateProject.isPending}
                        placeholder={t('portfolio.descriptionAr') || 'وصف المشروع (عربي)'}
                        className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300 resize-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="text-red-500">*</span>
                        {t('portfolio.descriptionEn') || 'Project Description (English)'}
                      </label>
                      <Textarea
                        value={formData.description_en}
                        onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                        rows={4}
                        required
                        disabled={createProject.isPending || updateProject.isPending}
                        placeholder={t('portfolio.descriptionEn') || 'Project Description (English)'}
                        className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Location Fields */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield size={16} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t('portfolio.location') || 'الموقع'}
                    </h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="text-red-500">*</span>
                        {t('portfolio.locationAr') || 'الموقع (عربي)'}
                      </label>
                      <Input
                        value={formData.location_ar}
                        onChange={(e) => setFormData({ ...formData, location_ar: e.target.value })}
                        required
                        disabled={createProject.isPending || updateProject.isPending}
                        placeholder={t('portfolio.locationAr') || 'الموقع (عربي)'}
                        className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="text-red-500">*</span>
                        {t('portfolio.locationEn') || 'Location (English)'}
                      </label>
                      <Input
                        value={formData.location_en}
                        onChange={(e) => setFormData({ ...formData, location_en: e.target.value })}
                        required
                        disabled={createProject.isPending || updateProject.isPending}
                        placeholder={t('portfolio.locationEn') || 'Location (English)'}
                        className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Completed Date */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield size={16} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t('portfolio.completedDate') || 'تاريخ الإنجاز'}
                    </h3>
                  </div>
                  
                  <div className="relative z-40">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <span className="text-red-500">*</span>
                      {t('portfolio.completedDate') || 'تاريخ الإنجاز'}
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          {t('portfolio.year') || 'السنة'}
                        </label>
                        <Input
                          type="number"
                          min="1900"
                          max="2100"
                          value={formData.completed_year}
                          onChange={(e) => setFormData({ ...formData, completed_year: e.target.value })}
                          required
                          disabled={createProject.isPending || updateProject.isPending}
                          placeholder={t('portfolio.year') || 'السنة'}
                          className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          {t('portfolio.month') || 'الشهر'}
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="12"
                          value={formData.completed_month}
                          onChange={(e) => setFormData({ ...formData, completed_month: e.target.value.padStart(2, '0') })}
                          required
                          disabled={createProject.isPending || updateProject.isPending}
                          placeholder={t('portfolio.month') || 'الشهر'}
                          className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          {t('portfolio.day') || 'اليوم'}
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="31"
                          value={formData.completed_day}
                          onChange={(e) => setFormData({ ...formData, completed_day: e.target.value.padStart(2, '0') })}
                          required
                          disabled={createProject.isPending || updateProject.isPending}
                          placeholder={t('portfolio.day') || 'اليوم'}
                          className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <ImageIcon size={16} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {t('portfolio.projectImages') || 'صور المشروع'}
                    </h3>
                  </div>
                  
                  {/* Drag and Drop Area */}
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer",
                      dragActive ? "border-primary bg-primary/5" : "border-slate-300 hover:border-primary/50 hover:bg-slate-50",
                      (createProject.isPending || updateProject.isPending) && "pointer-events-none opacity-50"
                    )}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Upload className="h-8 w-8 text-primary" />
                    </div>
                    <p className="text-slate-700 font-medium mb-2">
                      {t('portfolio.dragDropImages') || 'اسحب وأفلت الصور هنا أو انقر للاختيار'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {t('portfolio.imageFormats') || 'PNG, JPG, JPEG حتى 10MB لكل صورة'}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                  </div>

                  {/* Existing Images */}
                  {existingImages.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Shield size={16} className="text-slate-600" />
                        <h4 className="text-sm font-medium text-slate-700">
                          {t('portfolio.existingImages') || 'الصور الموجودة'}
                        </h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {existingImages.map((image) => (
                          <div key={image.id} className="relative group">
                            <div className="aspect-square rounded-xl overflow-hidden border border-slate-200">
                              <ProtectedImage
                                src={image.url}
                                alt="Project image"
                                containerClassName="w-full h-full"
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 bg-red-600 hover:bg-red-700"
                              onClick={() => removeExistingImage(image.id)}
                              disabled={createProject.isPending || updateProject.isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Images */}
                  {images.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Sparkles size={16} className="text-slate-600" />
                        <h4 className="text-sm font-medium text-slate-700">
                          {t('portfolio.newImages') || 'الصور الجديدة'}
                        </h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {images.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square rounded-xl overflow-hidden border border-slate-200">
                              <img
                                src={URL.createObjectURL(image)}
                                alt={`New image ${index + 1}`}
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 bg-red-600 hover:bg-red-700"
                              onClick={() => removeNewImage(index)}
                              disabled={createProject.isPending || updateProject.isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Image Count Info */}
                  {(existingImages.length > 0 || images.length > 0) && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                        <ImageIcon size={16} />
                        <span>
                          {t('portfolio.totalImages') || 'إجمالي الصور'}: {existingImages.length + images.length}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-4 pt-6 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard/portfolio')}
                    className="flex-1 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                    disabled={createProject.isPending || updateProject.isPending}
                  >
                    {t('common.cancel') || 'إلغاء'}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                    disabled={
                      createProject.isPending || 
                      updateProject.isPending ||
                      !formData.title_ar.trim() ||
                      !formData.title_en.trim() ||
                      !formData.description_ar.trim() ||
                      !formData.description_en.trim() ||
                      !formData.location_ar.trim() ||
                      !formData.location_en.trim() ||
                      !formData.completed_year ||
                      !formData.completed_month ||
                      !formData.completed_day
                    }
                  >
                    <Save size={16} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {createProject.isPending || updateProject.isPending
                      ? (t('common.loading') || 'جاري المعالجة...')
                      : isEditing
                      ? (t('portfolio.updateProjectBtn') || 'تحديث المشروع')
                      : (t('portfolio.addProjectBtn') || 'إضافة المشروع')
                    }
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AddEditProject;
